import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Star } from 'atoms/icons/Star';
import { StarBall } from 'atoms/icons/StarBall';
import { Horizontal, Vertical } from 'atoms/layout/flex';
import { BodyS, HeadlineM, LabelS } from 'atoms/typography';
import { FlagAvatar } from 'components/country/FlagAvatar';

const Root = styled(Vertical)`
  padding: var(--double-unit);
`;

const TitleWrapper = styled(Horizontal)`
  margin-bottom: var(--quadruple-unit);
`;

const TestimoniesContainer = styled(Horizontal)`
  flex-wrap: wrap;
`;

const TestimonyWrapper = styled.div`
  background: linear-gradient(
    to bottom,
    var(--c-white),
    var(--c-nd-700-opaque)
  );
  border-radius: var(--unit);
  padding: var(--double-unit);
  width: 25vw;
  min-width: 272px;
  height: calc(12 * var(--unit));
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  color: var(--c-black);
`;

const TestimonyTitleContainer = styled(Horizontal)`
  justify-content: space-between;
  align-items: center;
`;

const RatedStar = styled.span<{ isActive: boolean }>`
  color: ${({ isActive }) =>
    isActive ? 'var(--c-social-instagram)' : 'var(--c-nd-500-opaque)'};
  font-size: var(--t-24);
`;

export type CountryFlag = {
  name: string;
  slug: string;
};

type Testimony = {
  heid: string;
  slug: string;
  country: CountryFlag;
  rating: number;
  message: ReactNode;
};

type Props = { testimonies: Testimony[] };

const Rating = ({ rating }: { rating: number }) => {
  return (
    <Horizontal>
      {Array.from({ length: 5 }, (_, i) => (
        <RatedStar key={i} isActive={i < rating}>
          <Star />
        </RatedStar>
      ))}
    </Horizontal>
  );
};

const Testimony = ({ heid, slug, country, rating, message }: Testimony) => {
  return (
    <TestimonyWrapper id={heid}>
      <Vertical gap={2}>
        <TestimonyTitleContainer>
          <Horizontal>
            <LabelS bold>{slug}</LabelS>
            <FlagAvatar country={country} />
          </Horizontal>
          <Rating rating={rating} />
        </TestimonyTitleContainer>
        <BodyS>{message}</BodyS>
      </Vertical>
    </TestimonyWrapper>
  );
};

export const Testimonials = ({ testimonies }: Props) => {
  return (
    <Root>
      <TitleWrapper>
        <StarBall />
        <HeadlineM>
          <FormattedMessage
            id="landing.Testimonials.title"
            defaultMessage="Testimonials"
          />
        </HeadlineM>
      </TitleWrapper>
      <TestimoniesContainer center gap={4}>
        {testimonies.map(testimonial => (
          <Testimony
            key={testimonial.heid}
            heid={testimonial.heid}
            slug={testimonial.slug}
            country={testimonial.country}
            rating={testimonial.rating}
            message={testimonial.message}
          />
        ))}
      </TestimoniesContainer>
    </Root>
  );
};
